import React from 'react';

import { createStyles, Theme, withStyles, WithStyles } from '@material-ui/core';
import { FiltersDialog } from 'core';
import { TDateFilter } from 'core/FiltersDialog/types';
import { isEmpty, map, mapValues } from 'lodash';
import moment from 'moment';
import { useSelector } from 'react-redux';

import { addQuery } from '@lib/services/queryService';
import { selectCurrentMasterSettings } from '@lib/selectors';

import getCleanFilterComp from 'components/FixedFilters/helpers/getCleanFilterComp';
import getCleanFilterGraphQL from 'components/FixedFilters/helpers/getCleanFilterGraphQL';
import { BoxContentWithFilters } from 'components/v2';
import useURLSearchParams from 'hooks/useURLSearchParams';

import { EnumReminderType } from './__generated__/QueryTableRemindersQuery.graphql';
import QueryTableReminders, { QueryTableRemindersProps } from './QueryTableReminders';
import QueryTextFieldSearchPatient from './QueryTextFieldSearchPatient';

const styles = ({ spacing: { unit } }: Theme) =>
  createStyles({
    marginLeft: {
      marginLeft: unit * 2,
    },
  });

type TDefaultFilters = {
  reminderType: string[];
  dateRangeFuture: {
    selectedDate: string;
    fromDate: number;
    toDate: number;
  };
  facility?: string[];
};

const defaultFilters: TDefaultFilters = {
  reminderType: ['all'],
  dateRangeFuture: {
    selectedDate: 'today',
    fromDate: moment().valueOf(),
    toDate: moment()
      .endOf('day')
      .valueOf(),
  },
  facility: ['all'],
};

type TSearchParams = {
  facility?: string;
  type?: string;
  selectedDate?: string;
  fromDate?: string;
  toDate?: string;
  patient?: string;
  patientSearch?: string;
};

type TFilters = {
  facility?: string[];
  reminderType: string[];
  dateRangeFuture: TDateFilter;
};

const searchParamsToQueryVariables = (searchParams: TSearchParams): QueryTableRemindersProps['variables'] => {
  const { type, fromDate: initialFromDate, toDate, patient, patientSearch } = searchParams;

  // Ensure that dates are not in the past
  let fromDate = initialFromDate ? moment(parseInt(initialFromDate, 10)) : moment();
  if (fromDate.isBefore()) {
    fromDate = moment();
  }
  return {
    filter: {
      _operators: {
        type: getCleanFilterGraphQL<EnumReminderType>(type),
        sendAt: {
          gte: fromDate.valueOf(),
          lte: toDate ? moment(parseInt(toDate, 10)).valueOf() : defaultFilters.dateRangeFuture.toDate,
        },
        status: { nin: ['skipped'] },
        patient: getCleanFilterGraphQL(patient),
      },
      patient: isEmpty(patient) && patientSearch ? null : undefined,
    },
  };
};

const searchParamsToFilters = (searchParams: TSearchParams, isToUseSendAndReceiverFilters: boolean) => {
  const { type, selectedDate, fromDate, toDate, facility } = searchParams;
  return {
    reminderType: type ? getCleanFilterComp(type) : defaultFilters.reminderType,
    dateRangeFuture: {
      selectedDate: selectedDate || defaultFilters.dateRangeFuture.selectedDate,
      fromDate: fromDate ? moment(parseInt(fromDate, 10)).valueOf() : defaultFilters.dateRangeFuture.fromDate,
      toDate: toDate ? moment(parseInt(toDate, 10)).valueOf() : defaultFilters.dateRangeFuture.toDate,
    },
    ...(isToUseSendAndReceiverFilters && {
      facility: facility ? getCleanFilterComp(facility) : defaultFilters.facility,
    }),
  };
};

const filtersToSearchParams = (filters: TFilters): TSearchParams => ({
  type: filters.reminderType.join(','),
  ...mapValues(filters.dateRangeFuture, (v) => `${v}`),
  ...(filters.facility && { facility: filters.facility.join(',') }),
});

export interface WrapperQueryTableRemindersProps
  extends Omit<QueryTableRemindersProps, 'classes' | 'fragmentRef'>,
    WithStyles<typeof styles> {}

const WrapperQueryTableReminders: React.FC<WrapperQueryTableRemindersProps> = ({ classes, ...rest }) => {
  const settings = useSelector(selectCurrentMasterSettings);
  const isToUseSendAndReceiverFilters = settings?.communication?.filterMessagesBySenderAndReceiver;
  if (!isToUseSendAndReceiverFilters) {
    delete defaultFilters.facility;
  }

  const searchParams = useURLSearchParams();
  const initialFilters = searchParamsToFilters(searchParams, isToUseSendAndReceiverFilters);
  const queryVariables = searchParamsToQueryVariables(searchParams);

  return (
    <BoxContentWithFilters
      toolbarContent={
        <>
          <QueryTextFieldSearchPatient
            fullWidth
            autoFocus
            value={searchParams.patientSearch}
            onChange={(patientSearch, patients) => addQuery({ patient: map(patients, '_id').join(','), patientSearch })}
          />
          <div className={classes.marginLeft}>
            <FiltersDialog
              filterName="messagingActivityReminders"
              isLocked={false}
              initialFilters={initialFilters}
              resetFiltersShape={defaultFilters}
              onApplyFilters={(filters) => addQuery(filtersToSearchParams(filters))}
              hideLockButton
            />
          </div>
        </>
      }
      content={<QueryTableReminders variables={queryVariables} stickyHeader {...rest} />}
    />
  );
};

export default withStyles(styles)(WrapperQueryTableReminders);
